<template>
  <div
    class="tw-relative"
    :class="{
      'tw-flex tw-items-center tw-justify-between': !!label
    }"
  >
    <div v-if="label" class="tw-text-xs">
      {{ label }}
    </div>
    <button
      ref="colorPickerButton"
      class="tw-ml-4 tw-flex tw-h-7 tw-w-[130px] tw-cursor-pointer tw-items-center tw-overflow-hidden tw-rounded-md tw-border tw-text-2xs tw-leading-5 focus-within:tw-border-deep-orange-500 dark:tw-bg-neutral-900"
      :class="{
        'tw-cursor-not-allowed': disabled
      }"
      :disabled="disabled"
      @click="openColorPicker"
    >
      <input
        type="text"
        class="tw-h-7 tw-w-[80px] tw-flex-1 tw-border-none tw-font-mono tw-text-xs tw-text-black focus:tw-border-transparent focus:tw-ring-transparent dark:tw-text-white"
        :data-testid="label.replaceAll(' ', '-').toLowerCase()"
        :class="{
          'tw-cursor-pointer': !disabled,
          'tw-cursor-not-allowed': disabled
        }"
        :value="textInputValue"
        readonly
      />
      <div v-if="isTransparent" class="transparent-bg tw-h-7 tw-w-9" />
      <div v-else class="transparent-bg tw-h-7 tw-w-9 tw-border-l">
        <div class="tw-h-full tw-w-full" :style="{ 'background-color': currentColor.style }" />
      </div>
      <div
        v-if="isOpen"
        v-studio-click-outside="() => (isOpen = false)"
        class="tw-absolute tw-right-0 tw-top-8 tw-z-50 tw-w-auto tw-rounded-md tw-border tw-border-gray-200 tw-bg-white tw-p-0 tw-shadow-lg"
      >
        <div class="tw-space-y-4 tw-p-4">
          <div class="tw-flex tw-items-center tw-justify-between">
            <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Choose Color</h3>
          </div>

          <div class="tw-space-y-4">
            <div class="tw-flex tw-items-center tw-gap-2">
              <input
                :id="id"
                type="color"
                :required="required"
                :value="hexColor"
                class="tw-h-12 tw-w-12 tw-cursor-pointer tw-rounded-md tw-border tw-border-gray-300"
                @input="handleColorInput"
              />
              <input
                type="text"
                :value="hexInputValue"
                class="focus:tw-ring-primary tw-flex-1 tw-rounded-md tw-border tw-bg-white tw-px-2 tw-py-1.5 tw-font-mono tw-text-xs focus:tw-outline-none focus:tw-ring-1"
                placeholder="#000000"
                @focus="startHexEditing"
                @blur="finishHexEditing"
                @input="handleTextInput"
              />
            </div>

            <div class="tw-space-y-1">
              <span class="tw-text-xs tw-font-medium tw-text-gray-500">Opacity</span>
              <div class="tw-flex tw-items-center tw-gap-2">
                <div class="transparent-bg tw-relative tw-h-3 tw-flex-1 tw-rounded-full">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    aria-label="Opacity"
                    data-testid="opacity-slider"
                    :value="alphaPercent"
                    class="alpha-slider tw-absolute tw-inset-0 tw-h-3 tw-w-full tw-cursor-pointer tw-appearance-none tw-rounded-full"
                    :style="{ background: `linear-gradient(to right, transparent, ${opaqueStyle})` }"
                    @input="handleAlphaInput"
                  />
                </div>
                <input
                  type="number"
                  min="0"
                  max="100"
                  aria-label="Opacity percentage"
                  data-testid="opacity-input"
                  :value="alphaPercent"
                  class="tw-w-14 tw-rounded-md tw-border tw-bg-white tw-px-2 tw-py-1 tw-text-xs"
                  @blur="handleAlphaInput"
                />
                <span class="tw-text-xs tw-text-gray-500">%</span>
              </div>
            </div>

            <div v-if="brandKitColors.length > 0" class="tw-mt-3 tw-border-t tw-pt-3">
              <div class="tw-mb-3 tw-flex tw-items-center tw-gap-2">
                <TablerBrush class="tw-h-4 tw-w-4 tw-text-gray-500" />
                <span class="tw-text-xs tw-font-medium tw-text-gray-500">Brand Kit Colors</span>
              </div>
              <div class="tw-flex tw-max-w-[240px] tw-flex-wrap tw-gap-2">
                <button
                  v-for="(color, index) in brandKitColors"
                  :key="`color-${index}`"
                  type="button"
                  class="hover:tw-border-primary focus:tw-ring-primary tw-h-8 tw-w-8 tw-rounded-md tw-border-2 tw-border-gray-300 tw-transition-colors focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1"
                  :style="{ backgroundColor: color }"
                  :aria-label="`Select brand kit color ${color}`"
                  @click="handleBrandKitColorClick(color)"
                />
              </div>
            </div>
            <BrandKitGoToSelectPrompt
              v-else
              class="tw-mt-3 tw-border-t tw-pt-3"
              @goToSelectBrandKit="handleGoToSelectBrandKit"
            />
          </div>
        </div>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { BrandKit } from '@goldcast/api/backend';
import { computed, ref, watch } from 'vue';
import { useStore } from 'vuex';

import type { BrandKitColors } from '@/studio/brandKit/types';

import BrandKitGoToSelectPrompt from '@/studio/lookAndFeel/components/BrandKitGoToSelectPrompt.vue';
import TablerBrush from '@/studioComponents/Icons/tabler/TablerBrush.vue';
import { destructureRgbColor, rgbaToHex } from '@/utils/color';

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, default: '' },
  required: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  initialColor: { type: String, default: 'rgba(0, 0, 0, 1)' }
});

const emit = defineEmits<{
  colorChange: [color: { red: number; green: number; blue: number; alpha: number; style: string; hex?: string }];
}>();

const store = useStore();
const isOpen = ref(false);

const currentColor = ref(
  destructureRgbColor(props.initialColor) || { red: 0, green: 0, blue: 0, alpha: 1, style: 'rgba(0, 0, 0, 1)' }
);

watch(
  () => props.initialColor,
  newColor => {
    const destructured = destructureRgbColor(newColor);
    if (destructured) {
      currentColor.value = destructured;
    }
  }
);

const selectedBrandKit = computed<BrandKit | null>(() => store.state.studioBrandKit.selectedBrandKit);

const brandKitColors = computed<string[]>(() => {
  if (!selectedBrandKit.value?.colors) {
    return [];
  }
  const colors = selectedBrandKit.value.colors as BrandKitColors;

  return [...colors.primary, ...colors.secondary, ...colors.neutral];
});

const textInputValue = computed<string>(() => {
  return rgbaToHex(currentColor.value.style, true);
});

const hexColor = computed<string>(() => {
  return rgbaToHex(currentColor.value.style, false);
});

const isEditingHex = ref(false);
const hexInputValue = ref(hexColor.value);

watch(hexColor, newHex => {
  if (!isEditingHex.value) {
    hexInputValue.value = newHex;
  }
});

const isTransparent = computed<boolean>(() => {
  return currentColor.value.alpha === 0;
});

const alphaPercent = computed<number>(() => Math.round(currentColor.value.alpha * 100));

const opaqueStyle = computed<string>(() => {
  const { red, green, blue } = currentColor.value;
  return `rgb(${red}, ${green}, ${blue})`;
});

const HEX_6 = /^#[A-Fa-f0-9]{6}$/;
const HEX_8 = /^#[A-Fa-f0-9]{8}$/;

function openColorPicker() {
  if (props.disabled) {
    return;
  }
  isOpen.value = true;
}

function emitColorChange(colorString: string, keepAlpha = true) {
  const parsed = destructureRgbColor(colorString);
  if (!parsed) {
    return;
  }
  const destructured = keepAlpha
    ? destructureRgbColor(`rgba(${parsed.red}, ${parsed.green}, ${parsed.blue}, ${currentColor.value.alpha})`)
    : parsed;
  if (destructured) {
    currentColor.value = destructured;
    emit('colorChange', {
      ...destructured,
      hex: rgbaToHex(destructured.style, true)
    });
  }
}

function handleColorInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emitColorChange(target.value);
}

function startHexEditing() {
  isEditingHex.value = true;
}

function finishHexEditing() {
  isEditingHex.value = false;
  const value = hexInputValue.value.trim();
  if (value.match(/^#[A-Fa-f0-9]{3}$/)) {
    emitColorChange(value);
  } else if (!HEX_6.test(value)) {
    hexInputValue.value = hexColor.value;
  }
}

function handleTextInput(event: Event) {
  const target = event.target as HTMLInputElement;
  hexInputValue.value = target.value;
  const trimmed = target.value.trim();
  if (HEX_6.test(trimmed) || HEX_8.test(trimmed)) {
    // An 8-digit hex carries its own alpha.
    emitColorChange(trimmed, !HEX_8.test(trimmed));
  }
}

function handleAlphaInput(event: Event) {
  const percent = parseInt((event.target as HTMLInputElement).value, 10);
  if (Number.isNaN(percent)) {
    return;
  }
  const alpha = Math.min(100, Math.max(0, percent)) / 100;
  const { red, green, blue } = currentColor.value;
  emitColorChange(`rgba(${red}, ${green}, ${blue}, ${alpha})`, false);
}

function handleBrandKitColorClick(color: string) {
  emitColorChange(color);
}

function handleGoToSelectBrandKit() {
  isOpen.value = false;
  store.commit('branding/setSection', { sectionId: 'brandKit', moduleId: 'selectBrandKit' });
}
</script>

<style lang="scss" scoped>
.transparent-bg {
  background: url('../../../../tailwind/alpha-background.svg');
}

.alpha-slider::-webkit-slider-thumb {
  appearance: none;
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-radius: 9999px;
  background: transparent;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 30%);
}

.alpha-slider::-moz-range-thumb {
  width: 14px;
  height: 14px;
  border: 2px solid #fff;
  border-radius: 9999px;
  background: transparent;
  box-shadow: 0 0 0 1px rgb(0 0 0 / 30%);
}
</style>
